import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'lemai_onboarded';

// ── Audio test ─────────────────────────────────────────────────────────────────
function playTestBeep() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; osc.type = 'sine';
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.25);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
    setTimeout(() => ctx.close(), 800);
  } catch { /* ignore */ }
}

// ── Integration status check ────────────────────────────────────────────────────
interface IntegStatus { name: string; status: 'checking' | 'ok' | 'warn' | 'error'; detail: string; }

export default function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState('');
  const [audioTested, setAudioTested] = useState(false);
  const [integrations, setIntegrations] = useState<IntegStatus[]>([
    { name: 'ArmorIP Alarm Feed',  status: 'checking', detail: 'Checking connection…' },
    { name: 'Protege GX Access',   status: 'checking', detail: 'Checking connection…' },
    { name: 'Camera Health Check', status: 'checking', detail: 'Checking connection…' },
  ]);
  const navigate = useNavigate();

  // Check integrations on step 1
  useEffect(() => {
    if (step !== 1) return;
    const tok = localStorage.getItem('lemai_token') || '';
    const hdr = { Authorization: `Bearer ${tok}` };

    // ArmorIP
    fetch('/api/armorip/alarms', { headers: hdr })
      .then(r => {
        setIntegrations(prev => prev.map((i, idx) => idx === 0
          ? { ...i, status: r.ok ? 'ok' : 'warn', detail: r.ok ? 'Connected — receiving alarms' : 'Reachable but no data yet' }
          : i));
      }).catch(() => {
        setIntegrations(prev => prev.map((i, idx) => idx === 0
          ? { ...i, status: 'error', detail: 'Not reachable — check ArmorIP webhook' }
          : i));
      });

    // Protege
    fetch('/api/protege/controllers', { headers: hdr })
      .then(r => {
        setIntegrations(prev => prev.map((i, idx) => idx === 1
          ? { ...i, status: r.ok ? 'ok' : 'warn', detail: r.ok ? 'Connected to Protege GX' : 'Not configured — set up in Settings' }
          : i));
      }).catch(() => {
        setIntegrations(prev => prev.map((i, idx) => idx === 1
          ? { ...i, status: 'warn', detail: 'Not configured — optional for basic use' }
          : i));
      });

    // Camera health
    fetch('/api/health', { headers: hdr })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setIntegrations(prev => prev.map((i, idx) => idx === 2
          ? { ...i, status: data ? 'ok' : 'warn', detail: data ? `${data.cameras?.online ?? '—'}/${data.cameras?.total ?? '—'} cameras online` : 'Health monitor not configured' }
          : i));
      }).catch(() => {
        setIntegrations(prev => prev.map((i, idx) => idx === 2
          ? { ...i, status: 'warn', detail: 'Health monitor not yet configured' }
          : i));
      });
  }, [step]);

  function handleFinish() {
    if (orgName.trim()) localStorage.setItem('lemai_org_name', orgName.trim());
    localStorage.setItem(STORAGE_KEY, '1');
    navigate('/dashboard');
  }

  const statusColor = (s: IntegStatus['status']) =>
    s === 'ok' ? '#22c55e' : s === 'warn' ? '#f59e0b' : s === 'error' ? '#ef4444' : '#555555';
  const statusIcon = (s: IntegStatus['status']) =>
    s === 'ok' ? '✓' : s === 'warn' ? '⚠' : s === 'error' ? '✕' : '…';

  const STEPS = ['Welcome', 'Integrations', 'Audio', 'Ready'];

  return (
    <div style={{ minHeight:'100vh', background:'radial-gradient(ellipse at 20% 50%, rgba(30,58,138,0.3) 0%, transparent 60%), #060c1a', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
      {/* Grid bg */}
      <div style={{ position:'fixed', inset:0, opacity:0.03, backgroundImage:'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize:'40px 40px', pointerEvents:'none' }} />

      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'20px', width:'100%', maxWidth:'520px', overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.7)', position:'relative' }}>

        {/* Step indicator */}
        <div style={{ padding:'20px 24px 0', display:'flex', alignItems:'center', gap:'8px' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                <div style={{ width:'22px', height:'22px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:700, background: i < step ? 'rgba(34,197,94,0.15)' : i === step ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)', color: i < step ? '#22c55e' : i === step ? '#3b82f6' : '#222222', border:`1px solid ${i < step ? 'rgba(34,197,94,0.3)' : i === step ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.07)'}` }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span style={{ fontSize:'11px', fontWeight:600, color: i === step ? '#e2e8f0' : '#555555' }}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div style={{ flex:1, height:'1px', background: i < step ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.06)' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div style={{ padding:'32px 28px' }}>

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div>
              <div style={{ width:'52px', height:'52px', borderRadius:'5px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'20px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <h2 style={{ margin:'0 0 8px', fontSize:'22px', fontWeight:800, color:'white' }}>Welcome to Lemai</h2>
              <p style={{ color:'#555555', fontSize:'13px', lineHeight:'1.6', marginBottom:'24px' }}>
                Your intelligent security operations platform is ready. Let's take 60 seconds to verify your integrations are connected and your audio alerts are working.
              </p>
              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block', fontSize:'11px', color:'#555555', marginBottom:'6px', fontWeight:600 }}>YOUR ORGANISATION NAME (optional)</label>
                <input
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder="e.g. City of Parramatta"
                  style={{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'10px 14px', color:'white', fontSize:'13px', fontFamily:'inherit', outline:'none' }}
                />
                <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>This appears in the sidebar and on exported reports</div>
              </div>
              <button onClick={() => setStep(1)} style={{ width:'100%', padding:'12px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontWeight:700, fontSize:'14px', cursor:'pointer' }}>
                Get Started →
              </button>
            </div>
          )}

          {/* Step 1: Integrations */}
          {step === 1 && (
            <div>
              <h2 style={{ margin:'0 0 6px', fontSize:'18px', fontWeight:800, color:'white' }}>Integration Check</h2>
              <p style={{ color:'#555555', fontSize:'12px', marginBottom:'20px' }}>Verifying your connected systems. Warnings are fine — you can configure integrations later in Settings.</p>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'24px' }}>
                {integrations.map(intg => (
                  <div key={intg.name} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:`${statusColor(intg.status)}18`, border:`1px solid ${statusColor(intg.status)}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:700, color:statusColor(intg.status), flexShrink:0, animation: intg.status === 'checking' ? 'pulse 1.2s ease-in-out infinite' : undefined }}>
                      {statusIcon(intg.status)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>{intg.name}</div>
                      <div style={{ fontSize:'11px', color:'#555555', marginTop:'1px' }}>{intg.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:'10px' }}>
                <button onClick={() => setStep(0)} style={{ padding:'10px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontWeight:600, fontSize:'13px', cursor:'pointer' }}>Back</button>
                <button onClick={() => setStep(2)} style={{ flex:1, padding:'10px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontWeight:700, fontSize:'13px', cursor:'pointer' }}>Continue →</button>
              </div>
            </div>
          )}

          {/* Step 2: Audio */}
          {step === 2 && (
            <div>
              <h2 style={{ margin:'0 0 6px', fontSize:'18px', fontWeight:800, color:'white' }}>Audio Alert Test</h2>
              <p style={{ color:'#555555', fontSize:'12px', marginBottom:'24px' }}>
                Lemai plays distinct tones for Critical, High, and Medium alarms. Click the button below to test your speakers — make sure audio is not muted.
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'24px' }}>
                {[
                  { label: 'Critical Alarm', freq: 880, pulses: 3, color: '#ef4444' },
                  { label: 'High Alarm', freq: 660, pulses: 2, color: '#f59e0b' },
                  { label: 'Medium Alarm', freq: 440, pulses: 1, color: '#3b82f6' },
                ].map(tone => (
                  <button key={tone.label} onClick={() => { playTestBeep(); setAudioTested(true); }} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'#0d0d0d', border:`1px solid ${tone.color}30`, borderRadius:'4px', cursor:'pointer', textAlign:'left' }}>
                    <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:`${tone.color}18`, border:`1px solid ${tone.color}40`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tone.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'#e5e5e5' }}>{tone.label}</div>
                      <div style={{ fontSize:'11px', color:'#555555' }}>{tone.freq}Hz · {tone.pulses} pulse{tone.pulses > 1 ? 's' : ''} — click to play</div>
                    </div>
                  </button>
                ))}
              </div>
              {audioTested && (
                <div style={{ padding:'10px 14px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'4px', fontSize:'12px', color:'#22c55e', marginBottom:'16px' }}>
                  ✓ Audio is working. You can toggle sounds any time with the speaker button in the top bar.
                </div>
              )}
              <div style={{ display:'flex', gap:'10px' }}>
                <button onClick={() => setStep(1)} style={{ padding:'10px 20px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontWeight:600, fontSize:'13px', cursor:'pointer' }}>Back</button>
                <button onClick={() => setStep(3)} style={{ flex:1, padding:'10px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontWeight:700, fontSize:'13px', cursor:'pointer' }}>
                  {audioTested ? 'Continue →' : 'Skip for now →'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Ready */}
          {step === 3 && (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', fontSize:'28px' }}>✓</div>
              <h2 style={{ margin:'0 0 8px', fontSize:'22px', fontWeight:800, color:'white' }}>You're all set</h2>
              <p style={{ color:'#555555', fontSize:'13px', lineHeight:'1.6', marginBottom:'28px' }}>
                Lemai is ready to monitor your sites. Head to the SOC Dashboard to see live alarms, or explore the Executive Dashboard for a KPI overview.
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'20px' }}>
                {[
                  { label:'SOC Dashboard', sub:'Live alarm monitoring', path:'/m/soc', color:'#ef4444' },
                  { label:'Executive Dashboard', sub:'KPIs & site overview', path:'/m/executive', color:'#3b82f6' },
                  { label:'Jobs', sub:'Manage work orders', path:'/m/jobs', color:'#f59e0b' },
                  { label:'Settings', sub:'Configure integrations', path:'/settings', color:'#555555' },
                ].map(item => (
                  <button key={item.path} onClick={() => { localStorage.setItem(STORAGE_KEY, '1'); if (orgName.trim()) localStorage.setItem('lemai_org_name', orgName.trim()); navigate(item.path); }} style={{ padding:'14px', borderRadius:'4px', border:`1px solid ${item.color}25`, background:`${item.color}08`, cursor:'pointer', textAlign:'left' }}>
                    <div style={{ fontSize:'12px', fontWeight:700, color:'#e5e5e5', marginBottom:'2px' }}>{item.label}</div>
                    <div style={{ fontSize:'10px', color:'#555555' }}>{item.sub}</div>
                  </button>
                ))}
              </div>
              <button onClick={handleFinish} style={{ width:'100%', padding:'12px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontWeight:700, fontSize:'14px', cursor:'pointer' }}>
                Go to Dashboard
              </button>
            </div>
          )}

        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}

export function useOnboarded() {
  return localStorage.getItem(STORAGE_KEY) === '1';
}
